/* Product page behaviour. Port of the prototype's assets/product.js, minus the
   parts Liquid now does on the server (state branching, spec rows, siblings,
   "more from this brand") and minus the localStorage cart, which is Shopify's
   real cart here.

   What is left is the three things that genuinely need the client:
     1. gallery: keep dots and thumbnails in step with the scroll track, and
        make arrow keys step through it
     2. lightbox: open, step, close, trap Escape
     3. fulfilment: keep the one call to action's label honest, and carry the
        choice to the cart as a line item property

   No framework, no build step, delegated from the section root so a Section
   Rendering API re-render cannot orphan a listener. */

(function () {
  const pdp = document.querySelector('[data-jhs-pdp]');
  if (!pdp) return;

  /* ------------------------------------------------------------------ *
   * Gallery
   * ------------------------------------------------------------------ */

  const gallery = pdp.querySelector('[data-jhs-gallery]');
  const track = pdp.querySelector('[data-jhs-gallery-track]');

  if (gallery && track) {
    const slides = Array.from(track.querySelectorAll('[data-jhs-slide]'));
    const markers = Array.from(gallery.querySelectorAll('[data-jhs-gallery-go]'));

    const mark = (index) => {
      markers.forEach((marker) => {
        const on = Number(marker.dataset.jhsGalleryGo) === index;
        marker.classList.toggle('is-on', on);
        if (marker.classList.contains('jhs-gallery__thumb')) {
          if (on) marker.setAttribute('aria-current', 'true');
          else marker.removeAttribute('aria-current');
        }
      });
    };

    const goTo = (index, behavior) => {
      const clamped = Math.max(0, Math.min(slides.length - 1, index));
      track.scrollTo({ left: track.clientWidth * clamped, behavior: behavior || 'smooth' });
      mark(clamped);
      return clamped;
    };

    const currentIndex = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));

    gallery.addEventListener('click', (event) => {
      const marker = event.target.closest('[data-jhs-gallery-go]');
      if (!marker) return;
      goTo(Number(marker.dataset.jhsGalleryGo));
    });

    track.addEventListener(
      'scroll',
      () => {
        mark(currentIndex());
      },
      { passive: true }
    );

    /* Arrow keys step the gallery whenever focus is inside it. The thumbnails
       are real buttons, so tabbing to them and pressing Enter already works;
       this is the extra affordance, not the only one. */
    gallery.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      goTo(currentIndex() + (event.key === 'ArrowRight' ? 1 : -1));
    });

    /* ---------------------------------------------------------------- *
     * Lightbox
     * ---------------------------------------------------------------- */

    const lightbox = pdp.querySelector('[data-jhs-lightbox]');

    if (lightbox) {
      const lbTrack = lightbox.querySelector('[data-jhs-lightbox-track]');
      const count = lightbox.querySelector('[data-jhs-lightbox-count]');
      const total = lbTrack ? lbTrack.children.length : 0;
      let lbIndex = 0;
      let lastFocused = null;

      const setCount = () => {
        if (count) count.textContent = `${lbIndex + 1} / ${total}`;
      };

      const lbGo = (index, behavior) => {
        if (!lbTrack || total === 0) return;
        lbIndex = (index + total) % total;
        lbTrack.scrollTo({ left: lbTrack.clientWidth * lbIndex, behavior: behavior || 'smooth' });
        setCount();
      };

      const open = (index) => {
        lastFocused = document.activeElement;
        lightbox.hidden = false;
        document.body.style.overflow = 'hidden';
        requestAnimationFrame(() => {
          lbGo(index, 'auto');
          const close = lightbox.querySelector('[data-jhs-lightbox-close]');
          if (close) close.focus();
        });
      };

      const close = () => {
        lightbox.hidden = true;
        document.body.style.overflow = '';
        if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      };

      track.addEventListener('click', (event) => {
        const slide = event.target.closest('[data-jhs-slide]');
        if (!slide) return;
        open(Number(slide.dataset.jhsSlide));
      });

      lightbox.addEventListener('click', (event) => {
        if (event.target.closest('[data-jhs-lightbox-close]')) {
          close();
          return;
        }
        const step = event.target.closest('[data-jhs-lightbox-step]');
        if (step) {
          lbGo(lbIndex + Number(step.dataset.jhsLightboxStep));
          return;
        }
        if (event.target === lightbox || event.target.classList.contains('jhs-lightbox__slide')) {
          close();
        }
      });

      if (lbTrack) {
        lbTrack.addEventListener(
          'scroll',
          () => {
            lbIndex = Math.round(lbTrack.scrollLeft / Math.max(1, lbTrack.clientWidth));
            setCount();
          },
          { passive: true }
        );
      }

      document.addEventListener('keydown', (event) => {
        if (lightbox.hidden) return;
        if (event.key === 'Escape') close();
        if (event.key === 'ArrowRight') lbGo(lbIndex + 1);
        if (event.key === 'ArrowLeft') lbGo(lbIndex - 1);
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * Description: Read more / Read less on clamped brand copy
   * ------------------------------------------------------------------ */
  const descToggle = pdp.querySelector('[data-jhs-desc-toggle]');
  const desc = pdp.querySelector('[data-jhs-desc]');
  if (descToggle && desc) {
    descToggle.addEventListener('click', () => {
      const open = desc.classList.toggle('is-clamped') === false;
      descToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      descToggle.textContent = open ? 'Read less' : 'Read more';
    });
  }

  /* ------------------------------------------------------------------ *
   * Fulfilment: one call to action whose label follows the selection
   * ------------------------------------------------------------------ */

  const fulfil = pdp.querySelector('[data-jhs-fulfil]');
  if (!fulfil) return;

  const property = pdp.querySelector('[data-jhs-fulfil-property]');
  const cta = pdp.querySelector('.jhs-buy__cta');
  const availability = pdp.dataset.availability || '';

  const LABELS = {
    ship: 'Ship it to me',
    collect: 'Collect at the showroom',
  };

  const ctaText = (choice) => {
    if (availability === 'pre order') {
      return choice === 'collect' ? 'Pre-order, collect at the showroom' : 'Pre-order';
    }
    if (availability === 'back order') {
      return 'Order now';
    }
    return choice === 'collect' ? 'Add to cart, collect at the showroom' : 'Add to cart';
  };

  /* Horizon's add-to-cart button holds its label as a bare text node beside the
     "added" checkmark span, so the label is rewritten in place rather than by
     clobbering innerHTML and losing that span. */
  const setCtaText = (text) => {
    if (!cta || cta.disabled) return;
    for (const node of cta.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim() !== '') {
        node.nodeValue = text;
        return;
      }
    }
  };

  const sync = () => {
    const checked = fulfil.querySelector('[data-jhs-fulfil-input]:checked');
    const choice = checked ? checked.value : 'ship';
    if (property) property.value = LABELS[choice] || LABELS.ship;
    setCtaText(ctaText(choice));
  };

  fulfil.addEventListener('change', sync);
  sync();
})();
